import type { ChatSessionWireMessage } from '@onefootprint/request-types/dashboard';
import type { ChatMessage } from '../hooks/use-chat-ws';

const REQUEST_INTERRUPTED_BY_USER = '[Request interrupted by user]';

/**
 * Turn persisted wire-message rows into rendered ChatMessages. Each row's
 * `wireContent` is a single `WsMessage` — the exact shape the websocket streams
 * live — so persisted history renders through the same path as live messages, with
 * no SDK-transcript parsing. It's raw JSON (snake_case keys preserved via
 * `x_fp_preserve_keys`), passed through verbatim. Rows whose content isn't a single
 * object are dropped; a version-incompatible message that is still an object renders
 * defensively inside the per-message error boundary downstream.
 */
export const mapChatSessionMessages = (rows: ChatSessionWireMessage[]): ChatMessage[] =>
  restoreQuestionAnswerIds(
    rows.flatMap(row => {
      const message = row.wireContent as unknown;
      return message && typeof message === 'object' && !Array.isArray(message) ? [message as ChatMessage] : [];
    }),
  );

/**
 * Re-attach `question_id` to a user message answering an `AskUserQuestion`.
 * Percy-shipped wire messages already carry it (so this is a no-op for them), but the
 * server-side fallback for pre-wire-shipping sessions emits user rows with an empty
 * `question_id` — this rebuilds the link by position. Remove alongside that legacy
 * conversion path.
 */
const restoreQuestionAnswerIds = (messages: ChatMessage[]): ChatMessage[] => {
  let pendingQuestionId: string | undefined;

  return messages.map(message => {
    if (message.type === 'tool_use' && message.tool === 'AskUserQuestion') {
      pendingQuestionId = message.id;
      return message;
    }

    if (!pendingQuestionId || message.type !== 'user') return message;
    if (message.question_id) {
      if (message.question_id === pendingQuestionId) pendingQuestionId = undefined;
      return message;
    }
    if (message.content === REQUEST_INTERRUPTED_BY_USER) return message;

    const answeredMessage = { ...message, question_id: pendingQuestionId };
    pendingQuestionId = undefined;
    return answeredMessage;
  });
};
